/* eslint-disable @typescript-eslint/no-confusing-void-expression */
/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { SxProps } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import type {
  FieldValues,
  UseFormGetValues,
  UseFormTrigger,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { hasValidGeometry } from '@opengeoweb/form-fields';
import type { HeaderAction } from '@opengeoweb/shared';
import { dateUtils, useIsMounted } from '@opengeoweb/shared';
import type { CoreAppStore, layerTypes, mapTypes } from '@opengeoweb/store';
import {
  uiSelectors,
  uiTypes,
  useUpdateFeatureSyncLayers,
} from '@opengeoweb/store';
import { DrawFIRLand } from '@opengeoweb/theme';
import { LayerType } from '@opengeoweb/webmap';
import type { DrawMode, OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import {
  DRAWMODE,
  addGeoJSONProperties,
  addSelectionTypeToGeoJSON,
  defaultBox,
  defaultDelete,
  defaultPolygon,
  defaultLocation,
  getIcon,
} from '@opengeoweb/webmap-react';
import type {
  ATSRLocation,
  Airmet,
  AirmetConfig,
  AirmetFromBackend,
  AirmetFromFrontend,
  AviationPhenomenon,
  AviationProduct,
  CancelAirmet,
  CancelSigmet,
  CloudLevelMinMaxType,
  FIRConfigAirmet,
  FIRConfigSigmet,
  FIRLocationGeoJson,
  Firareas,
  ICAOLocation,
  LevelMinMaxType,
  MovementMinMaxType,
  ProductCanbe,
  ProductConfig,
  ProductStatus,
  ProductType,
  Sigmet,
  SigmetFromBackend,
  SigmetFromFrontend,
  SurfaceWindMinMaxType,
} from '../../types';
import {
  LevelUnits,
  ProductAction,
  ProductStatusDescription,
  StartOrEndDrawing,
  isInstanceOfCancelSigmet,
  isInstanceOfCancelSigmetOrAirmet,
} from '../../types';
import {
  featurePropsEnd,
  featurePropsStart,
} from '../MapViewGeoJson/constants';

export const getDefaultPhenomena = (
  productConfig: ProductConfig,
  currentFir?: string,
): {
  phenomena: AviationPhenomenon[];
} => {
  const defaultActiveFIR = currentFir || productConfig.active_firs[0]; // first FIR in active_firs is the default
  const currentFirArea = productConfig.fir_areas[defaultActiveFIR];
  const firPhenomena = currentFirArea.phenomenon || [];

  return {
    phenomena: firPhenomena,
  };
};

export const getHoursBeforeValidity = (
  phenomenon: string,
  fir: string,
  config: ProductConfig,
): number => {
  if (!fir) {
    return 4;
  }
  const {
    /* eslint-disable @typescript-eslint/naming-convention */
    hours_before_validity,
    va_hours_before_validity,
    tc_hours_before_validity,
  } = config.fir_areas[fir] as FIRConfigSigmet;

  switch (phenomenon) {
    // Tropical Cyclone
    case 'TC':
      return tc_hours_before_validity;
    // Volcanic ash cloud
    case 'VA_CLD':
      return va_hours_before_validity;
    default:
      return hours_before_validity;
  }
};

export const getMaxHoursOfValidity = (
  phenomenon: string,
  fir: string,
  config: ProductConfig,
): number => {
  if (!fir) {
    return 4;
  }
  const FIR = getFirArea(fir, config);
  const {
    /* eslint-disable @typescript-eslint/naming-convention */
    max_hours_of_validity,
    va_max_hours_of_validity,
    tc_max_hours_of_validity,
  } = config.fir_areas[FIR] as FIRConfigSigmet;

  switch (phenomenon) {
    case 'TC': // Tropical Cyclone
      return tc_max_hours_of_validity ?? 6;
    case 'VA_CLD': // Volcanic ash cloud
      return va_max_hours_of_validity ?? 6;
    default:
      return max_hours_of_validity ?? 4;
  }
};

export const getValidFromDelayTimeMinutesFromConfig = (
  config: ProductConfig,
): number => {
  return config.valid_from_delay_minutes;
};

export const getDefaultValidityMinutesFromConfig = (
  config: ProductConfig,
): number => {
  return config.default_validity_minutes;
};

export const getOmitChangeFromConfig = (config: ProductConfig): boolean => {
  return config.omit_change_va_and_rdoact_cloud === true;
};

export const getFirArea = (
  selectedFIR: string,
  productConfig: ProductConfig,
): string => {
  // If no FIR selected or FIR doesn't exist in list, select first FIR
  return selectedFIR && productConfig.fir_areas[selectedFIR] !== undefined
    ? selectedFIR
    : productConfig.active_firs[0];
};

export const getActiveFIRArea = (
  selectedFIR: string,
  productConfig: ProductConfig,
): FIRConfigSigmet | FIRConfigAirmet => {
  const FIR = getFirArea(selectedFIR, productConfig);
  return productConfig.fir_areas[FIR];
};

export const getMaxPolygonPoints = (
  selectedFIR: string,
  productConfig: ProductConfig,
): number => {
  const { max_polygon_points } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigSigmet | FIRConfigAirmet;

  return max_polygon_points ?? 7; // Fallback to 7 if max_polygon_points is not defined
};

export const hasMaxFeaturePoints = (
  geojson: GeoJSON.FeatureCollection,
  maxPoints: number,
): boolean => {
  const hasGeometry = hasValidGeometry(geojson);
  if (!hasGeometry) {
    return false; // No drawing, so no need to display the error
  }

  const [feature] = geojson.features;
  const { coordinates, type } = feature.geometry as GeoJSON.Polygon;

  if (type === 'Polygon' && coordinates[0].length > maxPoints) {
    return true;
  }

  return false;
};

export const DEFAULT_MINIMUM_SURFACE_LEVEL = 0;

export const getMinCloudLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number => {
  // Use first FIR in list if no FIR selected
  const { cloud_level_min } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return min value for selected unit - if not present in list, return 0 as we cannot go below the surface
  return cloud_level_min?.[unit as keyof CloudLevelMinMaxType]
    ? cloud_level_min![unit as keyof CloudLevelMinMaxType]!
    : DEFAULT_MINIMUM_SURFACE_LEVEL;
};

export const getMinCloudLowerLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number => {
  // Use first FIR in list if no FIR selected
  const { cloud_lower_level_min } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return min value for selected unit - if not present in list, return 0 as we cannot go below the surface
  return cloud_lower_level_min?.[unit as keyof CloudLevelMinMaxType]
    ? cloud_lower_level_min![unit as keyof CloudLevelMinMaxType]!
    : DEFAULT_MINIMUM_SURFACE_LEVEL;
};

export const getMaxCloudLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number | null => {
  // Use first FIR in list if no FIR selected
  const { cloud_level_max } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return max value for selected unit - if not present in list, return null
  return cloud_level_max?.[unit as keyof CloudLevelMinMaxType]
    ? cloud_level_max![unit as keyof CloudLevelMinMaxType]!
    : null;
};

export const getMaxCloudLowerLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number | null => {
  // Use first FIR in list if no FIR selected
  const { cloud_lower_level_max } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return max value for selected unit - if not present in list, return null
  return cloud_lower_level_max?.[unit as keyof CloudLevelMinMaxType]
    ? cloud_lower_level_max![unit as keyof CloudLevelMinMaxType]!
    : null;
};

// Retrieve max value from config - if not set, there is no max value for this unit so we return null
export const getMaxLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: ProductConfig,
): number | null => {
  // Use first FIR in list if no FIR selected
  const { level_max } = getActiveFIRArea(selectedFIR, productConfig);
  // Return max value for selected unit - if not present in list, return null
  return level_max?.[unit as keyof LevelMinMaxType]
    ? level_max![unit as keyof LevelMinMaxType]!
    : null;
};

// Retrieve min value from config - if not set, return zero
export const getMinLevelValue = (
  unit: string,
  selectedFIR: string,
  productConfig: ProductConfig,
): number => {
  // Use first FIR in list if no FIR selected
  const { level_min } = getActiveFIRArea(selectedFIR, productConfig);

  // Return min value for selected unit - if not present in list, return 0 as we cannot go below the surface
  return level_min?.[unit as keyof CloudLevelMinMaxType]
    ? level_min![unit as keyof CloudLevelMinMaxType]!
    : DEFAULT_MINIMUM_SURFACE_LEVEL;
};

export const getMaxWindSpeedValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number | null => {
  // Use first FIR in list if no FIR selected
  const { wind_speed_max } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return min value for selected unit - if not present in list, return null
  return wind_speed_max?.[unit as keyof SurfaceWindMinMaxType]
    ? wind_speed_max![unit as keyof SurfaceWindMinMaxType]!
    : null;
};

export const DEFAULT_MIN_WIND_SPEED = 0;
export const getMinWindSpeedValue = (
  unit: string,
  selectedFIR: string,
  productConfig: AirmetConfig,
): number => {
  // Use first FIR in list if no FIR selected
  const { wind_speed_min } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return min value for selected unit - if not present in list, return 0 as we have negative wind speed
  return wind_speed_min?.[unit as keyof SurfaceWindMinMaxType]
    ? wind_speed_min![unit as keyof SurfaceWindMinMaxType]!
    : DEFAULT_MIN_WIND_SPEED;
};

export const getMaxVisibilityValue = (
  selectedFIR: string,
  productConfig: AirmetConfig,
): number | null => {
  // Use first FIR in list if no FIR selected
  const { visibility_max } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return max value for selected unit - if not present in list, return null
  return visibility_max || null;
};

export const DEFAULT_MIN_VISIBILITY = 0;
export const getMinVisibilityValue = (
  selectedFIR: string,
  productConfig: AirmetConfig,
): number => {
  // Use first FIR in list if no FIR selected
  const { visibility_min } = getActiveFIRArea(
    selectedFIR,
    productConfig,
  ) as FIRConfigAirmet;

  // Return min value for selected unit - if not present in list, return 0 as we have negative wind speed
  return visibility_min || DEFAULT_MIN_VISIBILITY;
};

export const getAllFirAreas = (productConfig: ProductConfig): Firareas => {
  return productConfig.fir_areas;
};

export const getFirOptions = (
  productConfig: ProductConfig,
): { ATSR: ATSRLocation; FIR: ICAOLocation }[] => {
  const firAreas = getAllFirAreas(productConfig);
  const optionsFIR = Object.keys(firAreas).map((fir) => ({
    ATSR: firAreas[fir].location_indicator_atsr,
    FIR: firAreas[fir].fir_name,
  }));
  return optionsFIR;
};

export const getLevelInFeet = (level: number, unit: string): number => {
  if (LevelUnits[unit as keyof typeof LevelUnits] === 'm') {
    return level / 0.3048;
  }
  if (LevelUnits[unit as keyof typeof LevelUnits] === 'FL') {
    return level * 100;
  }
  return level;
};

export const isLevelLower = (
  ownValue: number | string,
  ownUnit: LevelUnits,
  otherValue: number,
  otherUnit: LevelUnits,
): boolean => {
  if (typeof ownValue === 'string' && !ownValue.length) {
    return true;
  }
  if (ownUnit === otherUnit && typeof ownValue === 'number') {
    return ownValue < otherValue;
  }

  // not same units - convert both to feet and compare
  const ownValueFeet = getLevelInFeet(ownValue as number, ownUnit);
  const otherValueFeet = getLevelInFeet(otherValue, otherUnit);

  return ownValueFeet < otherValueFeet;
};

export const getPhenomenon = (
  selectedFIR: string,
  phenomenon: string,
  productConfig: ProductConfig,
): string | undefined => {
  const FIR = getFirArea(selectedFIR, productConfig);
  const firArea = productConfig.fir_areas[FIR];
  if (!firArea) {
    return undefined;
  }

  const firPhenomenon = firArea.phenomenon?.find(
    (phen) => phen.code === phenomenon,
  );

  if (!firPhenomenon) {
    return undefined;
  }
  return firPhenomenon.description;
};

export const getDialogtitle = (
  t: TFunction,
  product: AviationProduct,
  productType: ProductType,
  productConfig: ProductConfig,
): string => {
  const productName = productType.toUpperCase();

  if (product === null) {
    return `New ${productName}`;
  }
  if (!isInstanceOfCancelSigmetOrAirmet(product)) {
    const phenomenon = getPhenomenon(
      product.firName,
      product.phenomenon,
      productConfig,
    );
    return `${productName} ${phenomenon || ''} - ${
      product.status === 'DRAFT'
        ? t('saved-as-draft')
        : ProductStatusDescription[product.status]
    }`;
  }
  if (isInstanceOfCancelSigmet(product)) {
    return `${productName} ${t('cancels')} ${product.cancelsSigmetSequenceId}`;
  }
  return `${productName} ${t('cancels')} ${product.cancelsAirmetSequenceId}`;
};

export const getProductFormMode = (
  canBeOptions: ProductCanbe[] | undefined,
  productListItem: SigmetFromBackend | AirmetFromBackend | null,
): string => {
  if (!productListItem) {
    return 'new';
  }

  if (!Array.isArray(canBeOptions)) {
    return 'view';
  }

  return canBeOptions.includes('PUBLISHED') ? 'edit' : 'view';
};

export const getLifecycleActions = ({
  canBe,
  onButtonPress,
  productType,
  areActionsDisabled = false,
  t,
}: ProductFormLifecycleButtonsProps & { t: TFunction }): HeaderAction[] => {
  const hasDiscardButton = canBe.includes('DISCARDED');
  const onDiscard = (): void => onButtonPress(ProductAction.DISCARD);

  const hasSaveButton = canBe.includes('DRAFTED');
  const onSave = (): void => onButtonPress(ProductAction.SAVE);

  const hasRenewButton = canBe.includes('RENEWED');
  const onRenew = (): void => onButtonPress(ProductAction.RENEW);

  const hasCancelButton = canBe.includes('CANCELLED');
  const onCancel = (): void => onButtonPress(ProductAction.CANCEL);

  const hasPublishButton = canBe.includes('PUBLISHED');
  const onPublish = (): void => onButtonPress(ProductAction.PUBLISH);

  const mobileMenuItems: HeaderAction[] = [
    ...(hasDiscardButton ? [{ text: t('discard'), action: onDiscard }] : []),
    ...(hasSaveButton
      ? [
          {
            text: t('save'),
            action: onSave,
            isDisabled: areActionsDisabled,
            variant: 'tertiary' as const,
          },
        ]
      : []),
    ...(hasRenewButton
      ? [
          {
            text: t('renew', {
              product: productType,
            }),
            action: onRenew,
            isDisabled: areActionsDisabled,
          },
        ]
      : []),
    ...(hasCancelButton
      ? [
          {
            text: `${t('cancel')} ${productType}`,
            action: onCancel,
            variant: 'tertiary' as const,
          },
        ]
      : []),
    ...(hasPublishButton
      ? [
          {
            text: t('publish'),
            action: onPublish,
            isDisabled: areActionsDisabled,
            variant: 'primary' as const,
          },
        ]
      : []),
  ];

  return mobileMenuItems;
};

interface ProductFormLifecycleButtonsProps {
  canBe: string[];
  productType: ProductType;
  onButtonPress: (newStatus: ProductAction) => void;
  areActionsDisabled?: boolean;
}

export const hasValue = (value: number | string): boolean =>
  value !== undefined &&
  value !== null &&
  value !== 'null' &&
  value.toString() !== 'NaN' &&
  value !== '';

export const prepareFormValues = <T extends AviationProduct>(product: T): T =>
  Object.keys(product).reduce((list, key) => {
    const value = product[
      key as keyof AviationProduct
    ] as unknown as GeoJSON.FeatureCollection;
    // remove hidden form values
    if (key === 'IS_DRAFT') {
      return list;
    }

    // remove geometry if empty coordinates
    if (
      key === 'endGeometry' ||
      key === 'endGeometryIntersect' ||
      key === 'startGeometry' ||
      key === 'startGeometryIntersect'
    ) {
      if (
        value?.features.length > 0 &&
        value?.features[0].geometry.type === 'Polygon' &&
        value?.features[0].geometry.coordinates[0].length
      ) {
        return { ...list, [key]: value };
      }
      if (
        value?.features.length > 0 &&
        value?.features[0].geometry.type === 'Point' &&
        value?.features[0].geometry.coordinates.length
      ) {
        return { ...list, [key]: value };
      }

      return list;
    }

    // check if nested object has value
    if (typeof value === 'object' && value !== null && value !== undefined) {
      const hasNaNValue = Object.keys(value).reduce(
        (total: boolean, valKey: string) => {
          const keyValue = value[valKey as keyof GeoJSON.FeatureCollection];
          return !hasValue(String(keyValue)) || total;
        },
        false,
      );

      return {
        ...list,
        ...(!hasNaNValue && {
          [key]: value,
        }),
      };
    }

    return {
      ...list,
      ...(hasValue(value) && {
        [key]: value,
      }),
    };
  }, {} as T);

export const createSigmetPostParameter = (
  sigmetToPost: Sigmet | CancelSigmet,
  newStatus: ProductStatus,
  previousUuid: string,
): SigmetFromFrontend => {
  const sigmetIncludingUuid =
    previousUuid !== null
      ? { ...sigmetToPost, uuid: previousUuid }
      : sigmetToPost;

  // Do not send empty numbered fields to the BE to prevent errors
  const sigmet = prepareFormValues(sigmetIncludingUuid);

  return {
    changeStatusTo: newStatus,
    sigmet,
  };
};

export const createAirmetPostParameter = (
  airmetToPost: Airmet | CancelAirmet,
  newStatus: ProductStatus,
  previousUuid: string,
): AirmetFromFrontend => {
  const airmetIncludingUuid =
    previousUuid !== null
      ? { ...airmetToPost, uuid: previousUuid }
      : airmetToPost;

  // Do not send empty numbered fields to the BE to prevent errors
  const airmet = prepareFormValues(airmetIncludingUuid);

  return {
    changeStatusTo: newStatus,
    airmet,
  };
};

export const useDelayedFormValues = (
  getValues: () => void,
  register: () => void,
  delay = 1000,
): [unknown, () => void] => {
  const { isMounted } = useIsMounted();
  const formValueTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [formValues, setFormValues] = React.useState(getValues());

  React.useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    setFormValues(getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, getValues]);

  const updateFormValueTimer = (): void => {
    if (!isMounted.current) {
      return;
    }
    setFormValues(getValues());
  };

  const onChangeForm = (): void => {
    clearTimeout(formValueTimer.current!);
    formValueTimer.current = setTimeout(updateFormValueTimer, delay);
  };

  return [formValues, onChangeForm];
};

export const getConfirmationDialogTitle = (
  t: TFunction,
  action: ProductAction,
  productType: ProductType,
): string => {
  const product = productType.toUpperCase();
  switch (action) {
    case ProductAction.CLOSE:
      return `${t('close')} ${product}`;
    case ProductAction.DISCARD:
      return `${t('discard-non-capitalized')} ${product}`;
    case ProductAction.PUBLISH:
    case ProductAction.AMEND:
      return t('publish');
    default:
      return `${t('cancel')} ${product}`;
  }
};

export const getConfirmationDialogContent = (
  t: TFunction,
  action: ProductAction,
  productType: ProductType,
): string => {
  const product = productType.toUpperCase();
  switch (action) {
    case ProductAction.DISCARD:
      return t('are-you-sure-you-want-to-discard', {
        product,
      });
    case ProductAction.AMEND:
      return t('are-you-sure-amend', { product });
    case ProductAction.CLOSE:
      return t('save-changes-made');
    case ProductAction.PUBLISH:
      return t('publish-this-product', { product });
    default:
      return t('cancel-this-product', { product });
  }
};

export const getConfirmationDialogButtonLabel = (
  t: TFunction,
  action: ProductAction,
  productType: ProductType,
): string => {
  const product = productType.toUpperCase();
  switch (action) {
    case ProductAction.DISCARD:
      return `${t('discard-product', { product })}`;
    case ProductAction.AMEND:
      return `${t('amend-product', { product })}`;
    case ProductAction.PUBLISH:
      return t('publish');
    case ProductAction.CANCEL:
      return t('yes-cancel', { product });
    default:
      return t('save-and-close');
  }
};

export const getConfirmationDialogCancelLabel = (
  t: TFunction,
  action: ProductAction,
): string => {
  switch (action) {
    case ProductAction.CLOSE:
      return t('discard-and-close');
    default:
      return undefined!;
  }
};

export const getProductIssueDate = (
  t: TFunction,
  initialProduct: Sigmet | Airmet,
  initialCancelProduct: CancelSigmet | CancelAirmet,
): string => {
  if (
    initialProduct !== null &&
    (initialProduct.status === 'PUBLISHED' ||
      initialProduct.status === 'EXPIRED') &&
    initialProduct.issueDate
  ) {
    return `${dateUtils.dateToString(
      dateUtils.utc(initialProduct.issueDate),
      dateUtils.DATE_FORMAT_DATEPICKER,
    )} UTC`;
  }

  if (initialCancelProduct?.issueDate) {
    return `${dateUtils.dateToString(
      dateUtils.utc(initialCancelProduct.issueDate),
      dateUtils.DATE_FORMAT_DATEPICKER,
    )} UTC`;
  }
  return t('not-published');
};

// Provide a cross section of the units from the config and the units as allowed by the type definition
// Units from the config which are not in the type definition should be ignored
export const extractUnitsToShow = (
  configUnits: string[],
  unitType: Record<string, string>,
): Record<string, string> => {
  return configUnits.reduce((list, unit) => {
    if (unitType[unit]) {
      return {
        ...list,
        [unit]: unitType[unit],
      };
    }
    return list;
  }, {});
};

export const getAllowedUnits = (
  selectedFIR: string,
  productConfig: ProductConfig,
  config_type: string,
  unitType: Record<string, string>,
): Record<string, string> => {
  // For selected FIR, find the corresponding unit_type. If not specified in the config, return as defined in type
  if (selectedFIR !== undefined && productConfig.fir_areas[selectedFIR]) {
    const firAllowedUnits = productConfig.fir_areas[selectedFIR].units;
    const allowedMovementUnits = firAllowedUnits.find(
      (unitConfig) => unitConfig.unit_type === config_type,
    );

    return allowedMovementUnits
      ? extractUnitsToShow(allowedMovementUnits.allowed_units, unitType)
      : unitType;
  }

  // If no FIR selected or FIR not in options - return all units as defined in the type
  return unitType;
};

/**
 * Function to translate the geojson data to a GeoJSON.FeatureCollection
 */
export const getFir = (
  productConfig: ProductConfig,
  firName?: string,
): FIRLocationGeoJson => {
  const currentFIRName = firName || productConfig.active_firs[0];
  const firLocation = productConfig.fir_areas[currentFIRName]?.fir_location;

  // When switching FIRs, the selectionType is not set, so we need to add it here to use 'Entire FIR' button
  const updatedFirLocationWithSelectionType = addSelectionTypeToGeoJSON(
    firLocation,
    firSelectionType,
  ) as FIRLocationGeoJson;

  return updatedFirLocationWithSelectionType;
};

/**
 * Function to check if the given geojson object has 'fir' selectionType property
 */
export const isFir = (geojson: GeoJSON.FeatureCollection): boolean => {
  if (!geojson?.features || geojson.features.length === 0) {
    return false;
  }
  const selectionType = geojson.features[0]?.properties?.['selectionType'];
  return (
    geojson?.features &&
    geojson.features.length > 0 &&
    selectionType === firSelectionType
  );
};

export const getFieldLabel = (
  t: TFunction,
  title: string,
  isDisabled: boolean,
  action = t('select'),
): string => (isDisabled ? title : `${action} ${title.toLowerCase()}`);

export const getBaseLayers = (
  productConfig: ProductConfig,
): layerTypes.Layer[] =>
  productConfig.mapPreset?.layers?.filter(
    (layer) =>
      layer.layerType === LayerType.baseLayer ||
      layer.layerType === LayerType.overLayer,
  ) || [];

export const getMaxMovementSpeedValue = (
  unit: string,
  selectedFIR: string,
  productConfig: ProductConfig,
): number | null => {
  const { movement_max } = getActiveFIRArea(selectedFIR, productConfig);

  // Return max value for selected unit - if not present in list, return null
  return movement_max?.[unit as keyof MovementMinMaxType]
    ? movement_max![unit as keyof MovementMinMaxType]!
    : null;
};

export const DEFAULT_MIN_MOVEMENT_SPEED = 0;

export const getMinMovementSpeedValue = (
  unit: string,
  selectedFIR: string,
  productConfig: ProductConfig,
): number => {
  const { movement_min } = getActiveFIRArea(selectedFIR, productConfig);

  // Return min value for selected unit - if not present in list, return DEFAULT_MIN_MOVEMENT_SPEED (zero, no negative speeds)
  return movement_min?.[unit as keyof MovementMinMaxType]
    ? movement_min![unit as keyof MovementMinMaxType]!
    : DEFAULT_MIN_MOVEMENT_SPEED;
};

/** Trigger form validations for given list of field names if field has a value */
export const triggerValidations = (
  fieldNames: string[],
  getValues: UseFormGetValues<FieldValues>,
  trigger: UseFormTrigger<FieldValues>,
): void => {
  fieldNames.forEach((field) => {
    const value = getValues(field);
    if (value || value === 0) {
      void trigger(field);
    }
  });
};

export const getProjection = (
  productConfig: ProductConfig,
): mapTypes.MapPreset['proj'] => productConfig.mapPreset?.proj || undefined;

export const firSelectionType = 'fir';

export const getDrawTools = (
  t: TFunction,
  type: StartOrEndDrawing,
  firShape: GeoJSON.FeatureCollection,
): DrawMode[] => {
  const defaultStyleProperties =
    type === StartOrEndDrawing.start ? featurePropsStart : featurePropsEnd;

  return [
    {
      ...defaultLocation,
      title: t('drop-a-point-as', { type }),
      shape: addGeoJSONProperties(
        defaultLocation.shape as GeoJSON.Feature,
        defaultStyleProperties,
      ),
    },
    {
      ...defaultBox,
      title: t('draw-a-box-as', { type }),
      shape: addGeoJSONProperties(
        defaultBox.shape as GeoJSON.Feature,
        defaultStyleProperties,
      ),
    },
    {
      ...defaultPolygon,
      title: t('draw-a-shape-as', { type }),
      shape: addGeoJSONProperties(
        defaultPolygon.shape as GeoJSON.Feature,
        defaultStyleProperties,
      ),
    },
    {
      drawModeId: 'drawtools-fir',
      title: t('set-a-fir-as', { type }),
      isSelectable: false,
      value: DRAWMODE.POLYGON,
      // add selectionType here to use later for `onUpdateStartgeoJSON` in Sigmet/Airmet-Form when updating form values directly
      shape: addSelectionTypeToGeoJSON(
        addGeoJSONProperties(firShape, { ...defaultStyleProperties }),
        firSelectionType,
      ),
      selectionType: firSelectionType,
    },
    {
      ...defaultDelete,
      title: t('remove-geometry'),
    },
  ];
};

export const updateDrawToolsWithFir = (
  drawTools: DrawMode[],
  newFirShape: GeoJSON.FeatureCollection,
): DrawMode[] => {
  return drawTools.map((mode) => {
    if (mode.selectionType === firSelectionType) {
      return {
        ...mode,
        shape: newFirShape,
      };
    }
    return mode;
  });
};

export const getToolIcon = (selectionType: string): React.ReactElement => {
  const defaultIcon = getIcon(selectionType);
  if (defaultIcon) {
    return defaultIcon;
  }

  if (selectionType === firSelectionType) {
    return <DrawFIRLand />;
  }
  return <DrawFIRLand />;
};

const LINKED_FORM_LAYER_IDS = new Set([
  'geojsonlayer-fir',
  'geojsonlayer-intersection-start',
  'geojsonlayer-intersection-end',
]);

export const useFormLayers = (layers: OpenLayersLayerProps[]): void => {
  const sourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.PublicWarnings),
  );

  const linkedFormLayersById = React.useMemo(
    () =>
      layers.reduce<Record<string, layerTypes.FeatureLayer>>(
        (layersById, layer) => {
          if (
            !layer.id ||
            !layer.geojson ||
            !LINKED_FORM_LAYER_IDS.has(layer.id)
          ) {
            return layersById;
          }

          return {
            ...layersById,
            [layer.id]: {
              geojson: layer.geojson,
            },
          };
        },
        {},
      ),
    [layers],
  );

  useUpdateFeatureSyncLayers(linkedFormLayersById, sourcePanelId);
};

export const getSequenceStyling = (
  product?: AviationProduct,
  hasTac = false,
): SxProps => {
  if (
    (hasTac && !product) ||
    product?.status === 'DRAFT' ||
    product?.status === ('' as ProductStatus)
  ) {
    return {
      fontWeight: 'lighter',
      color: 'geowebColors.typographyAndIcons.inactiveText',
    };
  }
  return {};
};

export const findSequenceNumber = (
  tac: string,
  noTac: string,
): string | null => {
  const tacLines = tac.split('\n');
  const hasTac = tacLines.length > 0 && tac !== noTac;

  const sequenceRegex = /^([A-Z]\d{2}|\d{1,2})$/;

  return hasTac
    ? (tac.split(/\s+/).find((word) => sequenceRegex.test(word)) ?? null)
    : null;
};
